import { Issue, CanvasPreview, FeaturePRD, Project } from "../../types/issues";
import { LabelInterface } from "../../types/labels";
import { User } from "../../types/users";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { SupabaseClient } from "@supabase/supabase-js";
import { onProjectsSnapshot } from "@/store/supabaseSlice";
import { KanbanDefaultStates } from "@/components/ProjectList/CanvasKanban";
import axios from "axios";
import store from "../store";
import type { Database } from "@/utils/supabase.types";
import { Canvas, Storyboard } from "@/utils/types";
import { useSupabaseStore } from "./supabaseStore";
import { toast } from "sonner";
import { StoryboardDelta, UpdateStoryboardsChange } from "./changeLedgerTypes";
import { createFilteredStorage } from "./utils";

type CanvasIdToIssue = Record<string, Issue>;
type SupabasePageRow = Omit<
  Database["public"]["Tables"]["pages"]["Row"],
  "storyboards"
> & {
  storyboards: Storyboard[];
};

/**
 * Helper to find storyboards with saved HTML previews for a set of canvases.
 * Handles both pages-based canvases and legacy canvas.storyboards fallback.
 */
async function findPreviewStoryboardsForCanvases(
  supabaseClient: SupabaseClient,
  canvasesWithStoryboards: Array<{
    id: string;
    storyboards: Storyboard[] | null;
  }>,
  pagesByCanvas: Record<string, SupabasePageRow[]>,
): Promise<Record<string, Storyboard[]>> {
  // Collect ALL storyboard IDs to check for previews
  const allStoryboardIds: string[] = [];
  for (const pages of Object.values(pagesByCanvas)) {
    for (const page of pages) {
      if (Array.isArray(page.storyboards)) {
        page.storyboards.forEach((sb) => {
          if (sb.id) allStoryboardIds.push(sb.id);
        });
      }
    }
  }
  // Add canvas.storyboards for canvases without pages (fallback)
  for (const canvas of canvasesWithStoryboards) {
    if (!pagesByCanvas[canvas.id] && Array.isArray(canvas.storyboards)) {
      canvas.storyboards.forEach((sb) => {
        if (sb.id) allStoryboardIds.push(sb.id);
      });
    }
  }

  if (allStoryboardIds.length === 0) {
    return {};
  }

  // Check which storyboards have saved HTML
  const { data: existingHtmlData } = await supabaseClient
    .from("saved_storyboard_html")
    .select("storyboard_id")
    .in("storyboard_id", allStoryboardIds);

  const storyboardIdsWithHtml = new Set(
    (existingHtmlData || []).map((row) => row.storyboard_id),
  );

  // Build result: storyboards with previews for each canvas
  const result: Record<string, Storyboard[]> = {};

  for (const canvas of canvasesWithStoryboards) {
    const canvasPages = pagesByCanvas[canvas.id];

    if (canvasPages && canvasPages.length > 0) {
      // Canvas has pages - find the latest page with previews
      // Pages should be ordered by updated_at descending
      for (const page of canvasPages) {
        if (!Array.isArray(page.storyboards) || page.storyboards.length === 0) {
          continue;
        }
        const hasSnapshots = page.storyboards.some((sb) =>
          storyboardIdsWithHtml.has(sb.id),
        );
        if (hasSnapshots) {
          result[canvas.id] = page.storyboards;
          break;
        }
      }
      // If no page has previews, store empty array (don't fallback)
      if (!result[canvas.id]) {
        result[canvas.id] = [];
      }
    } else {
      // No pages - fallback to canvas.storyboards
      const canvasStoryboards = canvas.storyboards || [];
      if (canvasStoryboards.length > 0) {
        const hasSnapshots = canvasStoryboards.some((sb) =>
          storyboardIdsWithHtml.has(sb.id),
        );
        result[canvas.id] = hasSnapshots ? canvasStoryboards : [];
      }
    }
  }

  return result;
}

interface IssuesState {
  issuesByProject: Record<string, CanvasIdToIssue>;
  canvasIdToIssue: CanvasIdToIssue;
  currentCanvas: Record<string, Canvas>;
  canvasIdToPreviewStoryboards: Record<string, Storyboard[]>;

  // For storyboard updates only
  sbUpdateLock: Record<string, boolean>;
  sbDBUpdateLock: Record<string, boolean>;

  projectStatusProcessingComplete: Record<string, boolean>;

  _convertToIssuesByProject: (
    issues: Issue[],
  ) => Record<string, CanvasIdToIssue>;
  _createMergedIssuesByProject: (
    issuesByProjectToMerge: Record<string, CanvasIdToIssue>,
  ) => Record<string, CanvasIdToIssue>;
  fetchCurrentCanvas: (
    supabaseClient: SupabaseClient | undefined,
    canvasId: string,
  ) => void;
  fetchPreviewStoryboardsForCanvas: (
    supabaseClient: SupabaseClient | undefined,
    canvasId: string,
  ) => Promise<Storyboard[] | null>;
  _fetchIssuePreviewsForProject: (
    supabaseClient: SupabaseClient | undefined,
    projectId: string,
    force: boolean,
  ) => Promise<{ bothPreviewsData: any | null }>;
  fetchPreviewStoryboardsForProject: (
    supabaseClient: SupabaseClient | undefined,
    projectId: string,
  ) => Promise<void>;
  fetchIssuesForProject: (
    supabaseClient: SupabaseClient | undefined,
    projectId: string,
    force: boolean,
  ) => void;
  deleteIssue: (
    supabaseClient: SupabaseClient | undefined,
    id: string,
    sessionToken?: string,
  ) => Promise<void>;
  bulkDeleteIssues: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    sessionToken?: string,
  ) => Promise<void>;
  updateIssueStatus: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    statusId: string,
  ) => void;
  updateIssuePriority: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    priority: number | null,
  ) => void;
  updateIssueAssignee: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    assignee: User | null,
  ) => void;
  createIssueLabel: (
    supabaseClient: SupabaseClient | undefined,
    project: Project,
    label: LabelInterface,
  ) => Promise<void>;
  updateIssueLabels: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    labels: LabelInterface[],
  ) => void;
  updateIssueEstimate: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    estimate: number | null,
  ) => void;
  updateIssueDueDate: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    dueDate: Date | null,
  ) => void;
  bulkUpdateIssueAssignee: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    assignee: User | null,
  ) => void;
  bulkUpdateIssuePriority: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    priority: number | null,
  ) => void;
  bulkUpdateIssueStatus: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    statusId: string,
  ) => void;
  bulkUpdateIssueEstimate: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    estimate: number | null,
  ) => void;
  bulkUpdateIssueLabel: (
    supabaseClient: SupabaseClient | undefined,
    issueIdToLabels: { issueId: string; labels: LabelInterface[] }[],
  ) => void;
  bulkUpdateIssueDueDate: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    dueDate: string | null,
  ) => void;
  bulkUpdateIssueOrder: (
    supabaseClient: SupabaseClient | undefined,
    prdsToUpdate: Partial<FeaturePRD>[],
  ) => void;
  createCanvas: (
    supabaseClient: SupabaseClient | undefined,
    token: string,
    projectId: string,
    canvasPayload: Canvas,
  ) => any;
  updateCanvas: (
    supabaseClient: SupabaseClient | undefined,
    canvasId: string,
    canvasPayload: Partial<Omit<Canvas, "storyboards">>,
  ) => void;

  // Try to make updating storyboards as atomic as possible
  updateCanvasStoryboardsInIssuesStore: (
    canvasId: string,
    storyboardDeltas: StoryboardDelta[],
    otherCanvasData?: Partial<Omit<Canvas, "storyboards">>,
    changeToAttachUndoTo?: UpdateStoryboardsChange,
  ) => void;

  createFeaturePRD: (
    supabaseClient: SupabaseClient | undefined,
    featurePRDPayload: FeaturePRD,
  ) => void;
  updateFeaturePRD: (
    supabaseClient: SupabaseClient | undefined,
    featurePRDPayload: FeaturePRD,
  ) => void;
  syncFeaturePrdData: (featurePrds: FeaturePRD[]) => void;
  syncCanvasDataForIssues: (canvases: CanvasPreview[]) => void;

  updateFeaturePrdStatuses: (
    supabaseClient: SupabaseClient | undefined,
    projectId: string,
    oldStatusName: string,
    newStatusName: string,
  ) => Promise<void>;
  updateUIStateWithNewStatuses: (
    oldStatusName: string,
    newStatusName: string,
    issuesToUpdate: Issue[],
  ) => void;

  isProjectStatusProcessingComplete: (projectId: string) => boolean;
}

// Filter to keep the latest 20 canvases or those updated in the last 2 weeks
const filterCurrentCanvasByRecent = (
  currentCanvas: Record<string, Canvas>,
): Record<string, Canvas> => {
  const twoWeeksAgo = Date.now() - 14 * 24 * 60 * 60 * 1000;

  const canvases = Object.values(currentCanvas);

  // Sort by last_updated (most recent first)
  const sortedCanvases = canvases.sort((a, b) => {
    const aTime = a.last_updated ? new Date(a.last_updated).getTime() : 0;
    const bTime = b.last_updated ? new Date(b.last_updated).getTime() : 0;
    return bTime - aTime;
  });

  // Keep first 20 or those within 2 weeks
  const filteredCanvases = sortedCanvases.filter((canvas, index) => {
    const canvasTime = canvas.last_updated
      ? new Date(canvas.last_updated).getTime()
      : 0;
    return index < 20 || canvasTime >= twoWeeksAgo;
  });

  return Object.fromEntries(filteredCanvases.map((c) => [c.id, c]));
};

export const useIssuesStore = create<IssuesState>()(
  persist(
    (set, get) => ({
      issuesByProject: {},
      canvasIdToIssue: {},
      currentCanvas: {},
      canvasIdToPreviewStoryboards: {},
      sbUpdateLock: {},
      sbDBUpdateLock: {},
      projectStatusProcessingComplete: {},

      // helper method
      _convertToIssuesByProject: (issues: Issue[]) => {
        const projectToIssueMap: Record<string, CanvasIdToIssue> = {};
        issues.forEach((issue) => {
          projectToIssueMap[issue.canvas.project_id] = {
            ...projectToIssueMap[issue.canvas.project_id],
            [issue.canvas.id]: issue,
          };
        });

        return projectToIssueMap;
      },
      fetchPreviewStoryboardsForProject: async (
        supabaseClient: SupabaseClient | undefined,
        projectId: string,
      ) => {
        if (!supabaseClient || !projectId) {
          return;
        }

        try {
          // Fetch canvases with their storyboards (for fallback when no pages exist)
          const { data: canvases, error: canvasError } = await supabaseClient
            .from("canvases")
            .select("id, storyboards")
            .eq("project_id", projectId)
            .is("deleted_at", null)
            .order("last_updated", { ascending: false })
            .limit(10);

          if (canvasError) {
            console.error("Error fetching canvases for project", canvasError);
            return;
          }

          const canvasIds = canvases?.map((canvas) => canvas.id) ?? [];
          if (canvasIds.length === 0) {
            return;
          }

          // Fetch pages for these canvases
          const { data: pagesData, error: pagesError } = await supabaseClient
            .from("pages")
            .select("*")
            .in("canvas_id", canvasIds)
            .is("deleted_at", null)
            .order("updated_at", { ascending: false });

          if (pagesError) {
            console.error("Error fetching pages for project", pagesError);
            return;
          }
          const pages = (pagesData || []) as SupabasePageRow[];

          // Group pages by canvas_id
          const pagesByCanvas: Record<string, SupabasePageRow[]> = {};
          for (const page of pages) {
            if (!pagesByCanvas[page.canvas_id]) {
              pagesByCanvas[page.canvas_id] = [];
            }
            pagesByCanvas[page.canvas_id].push(page);
          }

          // Use helper to find preview storyboards
          const canvasesWithStoryboards = (canvases || []).map((c) => ({
            id: c.id,
            storyboards: c.storyboards as Storyboard[] | null,
          }));
          const previewStoryboardsPerCanvas =
            await findPreviewStoryboardsForCanvases(
              supabaseClient,
              canvasesWithStoryboards,
              pagesByCanvas,
            );

          if (Object.keys(previewStoryboardsPerCanvas).length === 0) {
            return;
          }

          // Trigger full HTML fetch for storyboards we're storing (non-blocking)
          const storyboardIdsToFetch: string[] = [];
          for (const storyboards of Object.values(
            previewStoryboardsPerCanvas,
          )) {
            storyboards.forEach((sb) => {
              if (sb.id) storyboardIdsToFetch.push(sb.id);
            });
          }
          if (storyboardIdsToFetch.length > 0) {
            useSupabaseStore
              .getState()
              .fetchSavedStoryboardHtmlForStoryboards(storyboardIdsToFetch);
          }

          set((state) => ({
            canvasIdToPreviewStoryboards: {
              ...state.canvasIdToPreviewStoryboards,
              ...previewStoryboardsPerCanvas,
            },
          }));
        } catch (err) {
          console.error("Error fetching preview storyboards for project", err);
        }
      },
      // helper method
      _createMergedIssuesByProject: (
        issuesByProjectToMerge: Record<string, CanvasIdToIssue>,
      ) => {
        const state = get();

        return Object.fromEntries(
          Object.entries(issuesByProjectToMerge).map(
            ([key, canvasIdToIssueMap]) => [
              key,
              {
                ...state.issuesByProject[key],
                ...canvasIdToIssueMap,
              },
            ],
          ),
        );
      },
      syncFeaturePrdData: (featurePrds: FeaturePRD[]) => {
        set((state) => {
          const updates = {};
          for (const prd of featurePrds) {
            const issue = state.canvasIdToIssue[prd?.canvas_id ?? ""];
            if (issue) {
              updates[prd.canvas_id!] = {
                ...issue,
                featurePrd: { ...issue.featurePrd, ...prd },
              };
            }
          }

          return {
            issuesByProject: {
              ...state.issuesByProject,
              ...state._createMergedIssuesByProject(
                state._convertToIssuesByProject(Object.values(updates)),
              ),
            },
            canvasIdToIssue: { ...state.canvasIdToIssue, ...updates },
          };
        });
      },
      syncCanvasDataForIssues: (canvases: CanvasPreview[]) => {
        set((state) => {
          const updates = {};
          const updatedCurrentCanvas: Canvas | null = null;
          for (const canvas of canvases) {
            const issue = state.canvasIdToIssue[canvas.id];
            if (issue) {
              updates[canvas.id] = {
                ...issue,
                canvas: {
                  ...issue.canvas,
                  ...canvas,
                },
              };
            }
          }

          if (updatedCurrentCanvas) {
            return {
              canvasIdToIssue: { ...state.canvasIdToIssue, ...updates },
              issuesByProject: {
                ...state.issuesByProject,
                ...state._createMergedIssuesByProject(
                  state._convertToIssuesByProject(Object.values(updates)),
                ),
              },
            };
          } else {
            return {
              canvasIdToIssue: { ...state.canvasIdToIssue, ...updates },
              issuesByProject: {
                ...state.issuesByProject,
                ...state._createMergedIssuesByProject(
                  state._convertToIssuesByProject(Object.values(updates)),
                ),
              },
            };
          }
        });
      },
      _fetchIssuePreviewsForProject: async (
        supabaseClient: SupabaseClient | undefined,
        projectId: string,
        force: boolean = false,
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.warn("fetchIssuesForProject: Missing supabase client");
          return { bothPreviewsData: null };
        }

        if (!force && state.issuesByProject[projectId]) {
          return { bothPreviewsData: null };
        }

        const { data: bothPreviewsData, error: bothPreviewsError } =
          await supabaseClient.rpc("get_canvas_and_feature_prd_previews", {
            project_id_inp: projectId,
            env_inp: import.meta.env.VITE_ENVIRONMENT,
          });

        if (bothPreviewsError) {
          console.error(
            "Error fetching canvas and feature prds",
            bothPreviewsError,
          );
          return { bothPreviewsData: null };
        }

        return { bothPreviewsData };
      },
      fetchIssuesForProject: async (
        supabaseClient: SupabaseClient | undefined,
        projectId: string,
        force: boolean = false,
      ) => {
        const state = get();
        if (!supabaseClient) {
          console.warn("fetchIssuesForProject: Missing supabase client");
          return;
        }

        set((state) => ({
          projectStatusProcessingComplete: {
            ...state.projectStatusProcessingComplete,
            [projectId]: false,
          },
        }));

        if (!force && state.issuesByProject[projectId]) {
          return;
        }

        // Fetch canvas and feature prd previews
        const { bothPreviewsData } = await state._fetchIssuePreviewsForProject(
          supabaseClient,
          projectId,
          force,
        );

        if (!bothPreviewsData) {
          return;
        }

        // Build issues from previews
        const issues: CanvasIdToIssue = {};
        const canvasIdToFeaturePrd: Record<string, FeaturePRD> =
          bothPreviewsData.feature_prd_previews.reduce((acc, prd) => {
            acc[prd.canvas_id] = prd;
            return acc;
          }, {});

        bothPreviewsData.canvas_previews.forEach((canvas) => {
          const prd = canvasIdToFeaturePrd[canvas.id];
          if (prd) {
            // if prd status isnt valid, set it to backlog
            if (
              !Object.values(KanbanDefaultStates).includes(prd.status as any)
            ) {
              prd.status = KanbanDefaultStates.BACKLOG;
            }
            issues[canvas.id] = new Issue(canvas, prd);
          } else {
            // if no prd, create a new issue with no prd
            issues[canvas.id] = new Issue(canvas, undefined);
          }
        });

        // Update internal state right away
        set({
          issuesByProject: {
            ...state.issuesByProject,
            [projectId]: issues,
          },
          canvasIdToIssue: {
            ...state.canvasIdToIssue,
            ...Object.fromEntries(
              Object.values(issues).map((issue) => [issue.canvas.id, issue]),
            ),
          },
        });

        // Fetch full feature prds after first UI update
        const canvasIds = Object.values(issues).map((issue) => issue.canvas.id);
        const BATCH_SIZE = 100;
        const featurePrdsData: FeaturePRD[] = [];

        for (let i = 0; i < canvasIds.length; i += BATCH_SIZE) {
          const batch = canvasIds.slice(i, i + BATCH_SIZE);
          const { data, error } = await supabaseClient
            .from("feature_prd")
            .select("*")
            .in("canvas_id", batch);

          if (error) {
            console.error("Error fetching feature prds", error);
            return;
          }

          if (data) {
            featurePrdsData.push(...data);
          }
        }

        // Internal state can just be synced
        state.syncFeaturePrdData(featurePrdsData);

        // Fetch latest pages for the project in a non-blocking way, this is
        // only needed for the canvas preview once they open up a ticket
        state.fetchPreviewStoryboardsForProject(supabaseClient, projectId);

        set((state) => ({
          projectStatusProcessingComplete: {
            ...state.projectStatusProcessingComplete,
            [projectId]: true,
          },
        }));
      },
      bulkUpdateIssueEstimate: (
        supabaseClient: SupabaseClient | undefined,
        issueIds: string[],
        estimate: number | null,
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        // Save original PRDs
        const originalPrds = issueIds
          .map((issueId) => {
            const issue = state.canvasIdToIssue[issueId];
            if (!issue) return null;
            return issue.featurePrd;
          })
          .filter((prd): prd is NonNullable<typeof prd> => prd !== null);

        // Create updated PRDs
        const updatedPrds = originalPrds.map((prd) => ({
          ...prd,
          estimate: estimate ?? undefined,
        }));

        // Update UI optimistically
        state.syncFeaturePrdData(updatedPrds);

        // Update Supabase
        supabaseClient
          .from("feature_prd")
          .update({
            estimate: estimate,
          })
          .in(
            "id",
            originalPrds.map((prd) => prd.id),
          )
          .then(({ error }) => {
            if (error) {
              console.error("Error updating estimate:", error);
              // Roll back to original state
              state.syncFeaturePrdData(originalPrds);
              return;
            }
          });
      },
      updateIssueEstimate: (
        supabaseClient: SupabaseClient | undefined,
        issueId: string,
        estimate: number | null,
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        const issue = state.canvasIdToIssue[issueId];
        if (!issue) {
          return;
        }

        const featurePrd = issue.featurePrd;
        if (!featurePrd) {
          return;
        }

        // Create updated PRD
        const updatedPrd = {
          ...featurePrd,
          estimate: estimate ?? undefined,
        };

        // Update UI optimistically
        state.syncFeaturePrdData([updatedPrd]);

        // Update Supabase
        supabaseClient
          .from("feature_prd")
          .update({
            estimate: estimate,
          })
          .eq("id", featurePrd.id)
          .then(({ error }) => {
            if (error) {
              console.error("Error updating estimate:", error);
              // Roll back to original state
              state.syncFeaturePrdData([featurePrd]);
              return;
            }
          });
      },
      bulkDeleteIssues: async (
        supabaseClient: SupabaseClient | undefined,
        issueIds: string[],
        sessionToken?: string,
      ) => {
        const state = get();

        if (!supabaseClient) {
          return;
        }

        // filter out the issues that are not in the current project
        const issuesToKeep = Object.values(state.canvasIdToIssue).filter(
          (issue) => !issueIds.includes(issue.canvas.id),
        );

        // update the internal state by removing the issues from the projectIssues
        const newIssuesByProject =
          state._convertToIssuesByProject(issuesToKeep);
        const newCanvasIdToIssue = Object.fromEntries(
          issuesToKeep.map((issue) => [issue.canvas.id, issue]),
        );

        set({
          issuesByProject: newIssuesByProject,
          canvasIdToIssue: newCanvasIdToIssue,
        });

        try {
          // Soft delete the canvas by setting deleted_at
          const { error: updateError } = await supabaseClient
            .from("canvases")
            .update({
              deleted_at: new Date().toISOString(),
            })
            .in("id", issueIds);

          if (updateError) {
            console.error("Error deleting issue:", updateError);
            throw updateError;
          }

          // Hard delete the associated feature PRD
          const { error: prdDeleteError } = await supabaseClient
            .from("feature_prd")
            .delete()
            .in("canvas_id", issueIds);

          if (prdDeleteError) {
            console.error("Error deleting issue:", prdDeleteError);
            throw prdDeleteError;
          }

          // Update Redux store
          issueIds.forEach((id) => {
            axios
              .delete(
                `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${id}/cache`,
                {
                  headers: {
                    Authorization: `Bearer ${sessionToken}`,
                  },
                },
              )
              .catch((err) => {
                console.error("Error deleting canvas cache:", err.toString());
              });
          });
        } catch (error) {
          console.error("Error deleting issue:", error);
          // Restore the original state on error
          set({
            issuesByProject: state.issuesByProject,
            canvasIdToIssue: state.canvasIdToIssue,
          });
          throw error; // Re-throw the error so the calling code can handle it
        }
      },
      deleteIssue: async (
        supabaseClient: SupabaseClient | undefined,
        id: string,
        sessionToken?: string,
      ) => {
        const state = get();

        if (!supabaseClient) {
          return;
        }

        // update the internal state by removing the issue from the projectIssues
        const issuesToKeep = Object.values(state.canvasIdToIssue).filter(
          (issue) => issue.canvas.id !== id,
        );
        set({
          issuesByProject: {
            ...state.issuesByProject,
            ...state._createMergedIssuesByProject(
              state._convertToIssuesByProject(issuesToKeep),
            ),
          },
          canvasIdToIssue: {
            ...state.canvasIdToIssue,
            ...Object.fromEntries(
              issuesToKeep.map((issue) => [issue.canvas.id, issue]),
            ),
          },
        });

        try {
          // Soft delete the canvas by setting deleted_at
          const { error: updateError } = await supabaseClient
            .from("canvases")
            .update({
              deleted_at: new Date().toISOString(),
            })
            .eq("id", id);

          if (updateError) {
            console.error("Error deleting issue:", updateError);
            throw updateError;
          }

          // Hard delete the associated feature PRD
          const { error: prdDeleteError } = await supabaseClient
            .from("feature_prd")
            .delete()
            .eq("canvas_id", id);

          if (prdDeleteError) {
            console.error("Error deleting issue:", prdDeleteError);
            throw prdDeleteError;
          }

          axios
            .delete(
              `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${id}/cache`,
              {
                headers: {
                  Authorization: `Bearer ${sessionToken}`,
                },
              },
            )
            .catch((err) => {
              console.error("Error deleting canvas cache:", err.toString());
            });
        } catch (error) {
          console.error("Error deleting issue:", error);
          // re-insert the issue
          set({
            issuesByProject: {
              ...state.issuesByProject,
            },
            canvasIdToIssue: {
              ...state.canvasIdToIssue,
            },
          });
          return;
        }
      },
      bulkUpdateIssueStatus: (
        supabaseClient: SupabaseClient | undefined,
        issueIds: string[],
        statusId: string,
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        // Save original PRDs
        const originalPrds = issueIds
          .map((issueId) => {
            const issue = state.canvasIdToIssue[issueId];
            if (!issue) return null;
            return issue.featurePrd;
          })
          .filter((prd): prd is NonNullable<typeof prd> => prd !== null);

        const isCustomStatus = !Object.values(KanbanDefaultStates).some(
          (defaultStatus) =>
            defaultStatus.toLowerCase() === statusId.toLowerCase(),
        );

        let statusToUpdate: string;

        if (isCustomStatus) {
          statusToUpdate = statusId;
        } else {
          const statusKey = Object.entries(KanbanDefaultStates).find(
            ([_, value]) => value.toLowerCase() === statusId.toLowerCase(),
          )?.[0];

          if (!statusKey) {
            console.error("Could not find status key for", statusId);
            return;
          }

          statusToUpdate = KanbanDefaultStates[statusKey];
        }

        // Create updated PRDs
        const updatedPrds = originalPrds.map((prd) => ({
          ...prd,
          status: statusToUpdate,
        }));

        // Update UI optimistically
        state.syncFeaturePrdData(updatedPrds);

        // update supabase
        supabaseClient
          .from("feature_prd")
          .update({
            status: statusToUpdate,
          })
          .in(
            "id",
            originalPrds.map((prd) => prd.id),
          )
          .then(({ error }) => {
            if (error) {
              console.error("Error updating status:", error);
              // Roll back to original state
              state.syncFeaturePrdData(originalPrds);
              return;
            }
          });
      },
      updateIssueStatus: (
        supabaseClient: SupabaseClient | undefined,
        issueId: string,
        statusId: string,
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        const issue = state.canvasIdToIssue[issueId];
        if (!issue) {
          return;
        }

        // Save original PRD
        const originalPrd = issue.featurePrd;
        if (!originalPrd) {
          return;
        }

        const isCustomStatus = !Object.values(KanbanDefaultStates).some(
          (defaultStatus) =>
            defaultStatus.toLowerCase() === statusId.toLowerCase(),
        );

        let statusToUpdate: string;

        if (isCustomStatus) {
          statusToUpdate = statusId;
        } else {
          const statusKey = Object.entries(KanbanDefaultStates).find(
            ([_, value]) => value.toLowerCase() === statusId.toLowerCase(),
          )?.[0];

          if (!statusKey) {
            console.error("Could not find status key for", statusId);
            return;
          }

          statusToUpdate = KanbanDefaultStates[statusKey];
        }

        // Create updated PRD
        const updatedPrd = {
          ...originalPrd,
          status: statusToUpdate,
        };

        // Update UI optimistically
        state.syncFeaturePrdData([updatedPrd]);

        // Update Supabase
        supabaseClient
          .from("feature_prd")
          .update({
            status: statusToUpdate,
          })
          .eq("id", originalPrd.id)
          .then(({ error }) => {
            if (error) {
              console.error("Error updating status:", error);
              // Roll back to original state
              state.syncFeaturePrdData([originalPrd]);
              return;
            }
          });
      },
      bulkUpdateIssuePriority: (
        supabaseClient: SupabaseClient | undefined,
        issueIds: string[],
        priority: number | null,
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        // Save original PRDs
        const originalPrds = issueIds
          .map((issueId) => {
            const issue = state.canvasIdToIssue[issueId];
            if (!issue) return null;
            return issue.featurePrd;
          })
          .filter((prd): prd is NonNullable<typeof prd> => prd !== null);

        // Create updated PRDs
        const updatedPrds = originalPrds.map((prd) => ({
          ...prd,
          priority: priority ?? undefined,
        }));

        // Update UI optimistically
        state.syncFeaturePrdData(updatedPrds);

        // update supabase
        supabaseClient
          .from("feature_prd")
          .update({
            priority: priority,
          })
          .in(
            "id",
            originalPrds.map((prd) => prd.id),
          )
          .then(({ error }) => {
            if (error) {
              console.error("Error updating priority:", error);
              // Roll back to original state
              state.syncFeaturePrdData(originalPrds);
              return;
            }
          });
      },
      updateIssuePriority: (
        supabaseClient: SupabaseClient | undefined,
        issueId: string,
        priority: number | null,
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        const issue = state.canvasIdToIssue[issueId];
        if (!issue) {
          return;
        }

        // Save original PRD
        const originalPrd = issue.featurePrd;
        if (!originalPrd) {
          return;
        }

        // Create updated PRD
        const updatedPrd = {
          ...originalPrd,
          priority: priority !== null ? Number(priority) : undefined,
        };

        // Update UI optimistically
        state.syncFeaturePrdData([updatedPrd]);

        // Update Supabase
        supabaseClient
          .from("feature_prd")
          .update({
            priority: priority !== null ? Number(priority) : null,
          })
          .eq("id", originalPrd.id)
          .then(({ error }) => {
            if (error) {
              console.error("Error updating priority:", error);
              // Roll back to original state
              state.syncFeaturePrdData([originalPrd]);
              return;
            }
          });
      },
      bulkUpdateIssueAssignee: (
        supabaseClient: SupabaseClient | undefined,
        issueIds: string[],
        assignee: User | null,
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        // Get and save original canvases
        const originalCanvases = issueIds
          .map((issueId) => {
            const issue = state.canvasIdToIssue[issueId];
            if (!issue) return null;
            return issue.canvas;
          })
          .filter(
            (canvas): canvas is NonNullable<typeof canvas> => canvas !== null,
          );

        // Create updated canvases
        const updatedCanvases = originalCanvases.map((canvas) => ({
          ...canvas,
          assigned_user_id: assignee?.id || null,
          last_updated: new Date().toISOString(),
        }));

        // Update UI optimistically
        state.syncCanvasDataForIssues(updatedCanvases);

        // Update Supabase
        supabaseClient
          .from("canvases")
          .update({
            assigned_user_id: assignee?.id || null,
          })
          .in("id", issueIds)
          .select()
          .then(({ error }) => {
            if (error) {
              console.error("Error updating assignee:", error);
              // Roll back to original state
              state.syncCanvasDataForIssues(originalCanvases);
              return;
            }
          });
      },
      updateIssueAssignee: (
        supabaseClient: SupabaseClient | undefined,
        issueId: string,
        assignee: User | null,
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        const issue = state.canvasIdToIssue[issueId];
        if (!issue) {
          return;
        }

        // Save original canvas
        const originalCanvas = issue.canvas;
        if (!originalCanvas) {
          return;
        }

        // Create updated canvas
        const updatedCanvas = {
          ...originalCanvas,
          assigned_user_id: assignee?.id || null,
          last_updated: new Date().toISOString(),
        };

        // Update UI optimistically
        state.syncCanvasDataForIssues([updatedCanvas]);

        // Update Supabase
        supabaseClient
          ?.from("canvases")
          .update({
            assigned_user_id: assignee?.id || null,
          })
          .eq("id", updatedCanvas.id)
          .then(({ error }) => {
            if (error) {
              console.error("Error updating assignee:", error);
              // Roll back to original state
              state.syncCanvasDataForIssues([originalCanvas]);
              return;
            }
          });
      },
      createIssueLabel: async (
        supabaseClient: SupabaseClient | undefined,
        project: Project,
        label: LabelInterface,
      ) => {
        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        try {
          // Label goes into the project table, in the labels column
          const formattedLabel = {
            id: label.id,
            name: label.label.trim(),
            label: label.label.trim(),
            color: {
              key: label.label.toLowerCase().replace(/\s+/g, "-"),
              label: label.label.trim(),
              value: label.color.value,
            },
          };

          // convert labels JSON to array
          const currentProjectLabels =
            JSON.parse(JSON.stringify(project.labels)) || [];

          const updatedProjectLabels = [
            ...currentProjectLabels,
            formattedLabel,
          ];

          const addLabelResponse = await supabaseClient
            ?.from("projects")
            .update({
              labels: updatedProjectLabels,
            })
            .eq("id", project.id)
            .select()
            .single();

          if (!addLabelResponse || addLabelResponse.error) {
            return;
          }

          // Update Redux store with the new project data
          const updatedProject = addLabelResponse.data;
          store.dispatch(
            onProjectsSnapshot({
              newItems: [updatedProject],
              deletedItems: [],
            }),
          );
        } catch (error) {}
      },
      bulkUpdateIssueLabel: (
        supabaseClient: SupabaseClient | undefined,
        issueIdToLabels: { issueId: string; labels: LabelInterface[] }[],
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        // Save original canvases
        const originalCanvases = issueIdToLabels
          .map(({ issueId }) => {
            const issue = state.canvasIdToIssue[issueId];
            if (!issue) return null;
            return issue.canvas;
          })
          .filter(
            (canvas): canvas is NonNullable<typeof canvas> => canvas !== null,
          );

        // Create updated canvases
        const updatedCanvases = originalCanvases.map((canvas, index) => ({
          ...canvas,
          labels: issueIdToLabels[index].labels.map((label) => label.id),
        }));

        // Update UI optimistically
        state.syncCanvasDataForIssues(updatedCanvases);

        const updatePromises = issueIdToLabels.map((issueIdAndLabel) => {
          const issueId = issueIdAndLabel.issueId;
          const labelIDs = issueIdAndLabel.labels.map((label) => label.id);

          return supabaseClient
            .from("canvases")
            .update({ labels: labelIDs })
            .eq("id", issueId)
            .select()
            .single();
        });

        Promise.all(updatePromises)
          .then((results) => {
            const errors = results.filter((result) => result.error);
            if (errors.length > 0) {
              console.error("Errors in bulk update labels:", errors);
              // Roll back to original state
              state.syncCanvasDataForIssues(originalCanvases);
            }
          })
          .catch((error) => {
            console.error("Error in bulk update labels:", error);
            // Roll back to original state
            state.syncCanvasDataForIssues(originalCanvases);
          });
      },
      updateIssueLabels: async (
        supabaseClient: SupabaseClient | undefined,
        issueId: string,
        labels: LabelInterface[],
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        const issue = state.canvasIdToIssue[issueId];
        if (!issue) {
          return;
        }

        // Save original canvas
        const originalCanvas = issue.canvas;
        if (!originalCanvas) {
          return;
        }

        // Create updated canvas
        const updatedCanvas = {
          ...originalCanvas,
          labels: labels.map((label) => label.id),
        };

        // Update UI optimistically
        state.syncCanvasDataForIssues([updatedCanvas]);

        // Update Supabase
        const response = await supabaseClient
          ?.from("canvases")
          .update({
            labels: labels.map((label) => label.id),
          })
          .eq("id", originalCanvas.id)
          .select()
          .single();

        if (!response || response.error) {
          // Roll back to original state
          state.syncCanvasDataForIssues([originalCanvas]);
          return;
        }
      },
      bulkUpdateIssueDueDate: (
        supabaseClient: SupabaseClient | undefined,
        issueIds: string[],
        dueDate: string | null,
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        // Save original PRDs
        const originalPrds = issueIds
          .map((issueId) => {
            const issue = state.canvasIdToIssue[issueId];
            if (!issue) return null;
            return issue.featurePrd;
          })
          .filter((prd): prd is NonNullable<typeof prd> => prd !== null);

        // Create updated PRDs
        const updatedPrds = originalPrds.map((prd) => ({
          ...prd,
          due_date: dueDate ?? undefined,
        }));

        // Update UI optimistically
        state.syncFeaturePrdData(updatedPrds);

        supabaseClient
          .from("feature_prd")
          .update({
            due_date: dueDate,
          })
          .in(
            "id",
            originalPrds.map((prd) => prd.id),
          )
          .then(({ error }) => {
            if (error) {
              console.error("Error updating due date:", error);
              // Roll back to original state
              state.syncFeaturePrdData(originalPrds);
              return;
            }
          });
      },
      updateIssueDueDate: (
        supabaseClient: SupabaseClient | undefined,
        issueId: string,
        dueDate: Date | null,
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        const issue = state.canvasIdToIssue[issueId];
        if (!issue) {
          return;
        }

        // Save original PRD
        const originalPrd = issue.featurePrd;
        if (!originalPrd) {
          return;
        }

        // Create updated PRD
        const updatedPrd = {
          ...originalPrd,
          due_date: dueDate?.toISOString() ?? undefined,
        };

        // Update UI optimistically
        state.syncFeaturePrdData([updatedPrd]);

        // Update Supabase
        supabaseClient
          .from("feature_prd")
          .update({
            due_date: dueDate?.toISOString() ?? null,
          })
          .eq("id", originalPrd.id)
          .then(({ error }) => {
            if (error) {
              console.error("Error updating due date:", error);
              // Roll back to original state
              state.syncFeaturePrdData([originalPrd]);
              return;
            }
          });
      },
      bulkUpdateIssueOrder: (
        supabaseClient: SupabaseClient | undefined,
        prdsToUpdate: Partial<FeaturePRD>[],
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        if (prdsToUpdate.length === 0) {
          return;
        }

        const updatePromises = prdsToUpdate.map(async (partialPrd) => {
          if (!partialPrd.canvas_id) {
            console.error("Canvas ID is required for PRD update");
            return;
          }

          const issue = state.canvasIdToIssue[partialPrd.canvas_id];
          if (!issue) {
            console.error(
              `Issue not found for canvas_id: ${partialPrd.canvas_id}`,
            );
            return;
          }

          const existingPrd = issue.featurePrd;

          if (!existingPrd) {
            if (!partialPrd.id) {
              console.error("PRD ID is required for creation");
              return;
            }

            const newPrd: FeaturePRD = {
              id: partialPrd.id,
              canvas_id: partialPrd.canvas_id,
              ...partialPrd,
            };

            state.syncFeaturePrdData([newPrd]);

            const { error } = await supabaseClient
              .from("feature_prd")
              .insert(newPrd);

            if (error) {
              console.error("Error creating feature PRD:", error);
              return;
            }
          } else {
            const updatedPrd = {
              ...existingPrd,
              ...partialPrd,
            };

            state.syncFeaturePrdData([updatedPrd]);

            const { error } = await supabaseClient
              .from("feature_prd")
              .update(partialPrd)
              .eq("canvas_id", partialPrd.canvas_id);

            if (error) {
              console.error("Error updating feature PRD:", error);
              state.syncFeaturePrdData([existingPrd]);
              return;
            }
          }
        });

        // Execute all updates
        Promise.all(updatePromises).catch((error) => {
          console.error("Error in bulk update issue order:", error);
        });
      },
      createCanvas: async (
        supabaseClient: SupabaseClient | undefined,
        token: string,
        projectId: string,
        canvasPayload: Canvas,
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        try {
          const res = await axios.post(
            `${
              import.meta.env.VITE_BACKEND_BASE_URL
            }/projects/${projectId}/canvas`,
            canvasPayload,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          if (res.data.canvas) {
            set({
              issuesByProject: {
                ...state.issuesByProject,
                [res.data.canvas.project_id ?? ""]: {
                  ...state.issuesByProject[res.data.canvas.project_id ?? ""],
                  [res.data.canvas.id]: new Issue(res.data.canvas, undefined),
                },
              },
              canvasIdToIssue: {
                ...state.canvasIdToIssue,
                [res.data.canvas.id]: new Issue(res.data.canvas, undefined),
              },
            });
          }

          return res.data.canvas;
        } catch (error) {
          console.error("Error creating canvas:", error);
          // nothing to roll back
          return null;
        }
      },
      fetchCurrentCanvas: (
        supabaseClient: SupabaseClient | undefined,
        canvasId: string,
      ): void => {
        if (!supabaseClient || !canvasId) {
          console.error("supabase or canvas id not initialized");
          return;
        }

        // fetch the canvas from the db
        supabaseClient
          .from("canvases")
          .select("*")
          .eq("id", canvasId)
          .single()
          .then(({ data, error }) => {
            if (error) {
              console.error("Error fetching canvas:", error);
              return;
            }

            set({
              currentCanvas: {
                [canvasId]: data,
              },
            });
          });
      },
      fetchPreviewStoryboardsForCanvas: async (
        supabaseClient: SupabaseClient | undefined,
        canvasId: string,
      ) => {
        if (!supabaseClient || !canvasId) {
          return null;
        }

        try {
          // Fetch canvas with storyboards (for fallback when no pages exist)
          const { data: canvasData, error: canvasError } = await supabaseClient
            .from("canvases")
            .select("id, storyboards")
            .eq("id", canvasId)
            .single();

          if (canvasError) {
            console.error("Error fetching canvas", canvasId, canvasError);
            return null;
          }

          // Fetch all pages for this canvas, ordered by updated_at descending
          const { data: pagesData, error: pagesError } = await supabaseClient
            .from("pages")
            .select("*")
            .eq("canvas_id", canvasId)
            .is("deleted_at", null)
            .order("updated_at", { ascending: false });

          if (pagesError) {
            console.error(
              "Error fetching pages for canvas",
              canvasId,
              pagesError,
            );
            return null;
          }

          // Group pages by canvas_id (just one canvas here)
          const pagesByCanvas: Record<string, SupabasePageRow[]> = {};
          if (pagesData && pagesData.length > 0) {
            pagesByCanvas[canvasId] = pagesData as SupabasePageRow[];
          }

          // Use helper to find preview storyboards
          const canvasesWithStoryboards = [
            {
              id: canvasData.id,
              storyboards: canvasData.storyboards as Storyboard[] | null,
            },
          ];
          const result = await findPreviewStoryboardsForCanvases(
            supabaseClient,
            canvasesWithStoryboards,
            pagesByCanvas,
          );

          const foundStoryboards = result[canvasId] ?? [];

          set((state) => ({
            canvasIdToPreviewStoryboards: {
              ...state.canvasIdToPreviewStoryboards,
              [canvasId]: foundStoryboards,
            },
          }));

          return foundStoryboards;
        } catch (err) {
          console.error(
            "Error fetching preview storyboards for canvas",
            canvasId,
            err,
          );
          return null;
        }
      },
      updateCanvas: async (
        supabaseClient: SupabaseClient | undefined,
        canvasId: string,
        canvasPayload: Partial<Omit<Canvas, "storyboards">>,
      ) => {
        // synchronize the issue
        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        const issue = get().canvasIdToIssue[canvasId];
        if (!issue) {
          // Wait for the issue to load then update
          const unsubscribe = useIssuesStore.subscribe((state) => {
            const issue = state.canvasIdToIssue[canvasId];
            if (!issue) {
              return;
            }
            unsubscribe();
            state.updateCanvas(supabaseClient, canvasId, canvasPayload);
          });
          return;
        }

        // Save original state
        const originalCanvas = issue.canvas;
        const originalCurrentCanvas = get().currentCanvas[canvasId];

        // Update UI optimistically
        set((state) => ({
          currentCanvas: {
            ...state.currentCanvas,
            [canvasId]: {
              ...state.currentCanvas[canvasId],
              ...canvasPayload,
            },
          },
        }));

        // convert canvasPayload to a CanvasPreview
        const payloadAsPreview: Partial<CanvasPreview> = {
          id: canvasPayload.id,
          assigned_user_id: canvasPayload.assigned_user_id ?? "",
          labels: canvasPayload.labels as string[],
          project_id: canvasPayload.project_id ?? "",
          env: canvasPayload.env ?? "",
          current_branch: canvasPayload.current_branch ?? "",
          owner_user_id: canvasPayload.owner_user_id ?? "",
          last_updated: canvasPayload.last_updated ?? "",
        };

        // merge canvasPayload with canvas
        const mergedCanvasPreview = {
          ...issue.canvas,
          ...payloadAsPreview,
        };

        get().syncCanvasDataForIssues([mergedCanvasPreview]);

        const onError = (error) => {
          console.error("Error updating canvas:", error);
          // Roll back to original state
          get().syncCanvasDataForIssues([originalCanvas]);
          if (originalCurrentCanvas) {
            set({
              currentCanvas: {
                [canvasId]: originalCurrentCanvas,
              },
            });
          }
        };

        try {
          await supabaseClient
            .from("canvases")
            .update(canvasPayload)
            .eq("id", canvasId)
            .then(({ error }) => {
              if (error) {
                onError(error);
              }
            });
        } catch (error) {
          onError(error);
        }
      },
      updateCanvasStoryboardsInIssuesStore: async (
        canvasId: string,
        storyboardDeltas: StoryboardDelta[],
        otherCanvasData?: Partial<Omit<Canvas, "storyboards">>,
        changeToAttachUndoTo?: UpdateStoryboardsChange,
      ) => {
        // Only allow one update at a time
        const lock = get().sbUpdateLock[canvasId];
        if (lock) {
          // wait for the lock to be released
          const unsubscribe = useIssuesStore.subscribe((state) => {
            const lock = state.sbUpdateLock[canvasId];
            if (!lock) {
              unsubscribe();
              get().updateCanvasStoryboardsInIssuesStore(
                canvasId,
                storyboardDeltas,
                otherCanvasData,
                changeToAttachUndoTo,
              );
            }
          });
          return;
        }

        set({
          sbUpdateLock: {
            ...get().sbUpdateLock,
            [canvasId]: true,
          },
        });

        const currentCanvas = get().currentCanvas[canvasId];
        if (!currentCanvas) {
          return;
        }

        let newStoryboards: Storyboard[] = [];
        try {
          const { newStoryboards: appliedNewStoryboards, undoDeltas } =
            applyStoryboardDeltas(
              storyboardDeltas,
              currentCanvas.storyboards,
              changeToAttachUndoTo?.changeFields.valuesAtStartOfAction,
            );

          newStoryboards = appliedNewStoryboards;
          if (changeToAttachUndoTo) {
            changeToAttachUndoTo.instantUpdateData = {
              undoDeltas,
            };
          }

          // Optimistically update the UI
          set((state) => ({
            currentCanvas: {
              ...state.currentCanvas,
              [canvasId]: {
                ...state.currentCanvas[canvasId],
                storyboards: newStoryboards,
                ...(otherCanvasData || {}),
              },
            },
          }));

          // Update the preview deterministically
          if (otherCanvasData) {
            // convert otherCanvasData to a CanvasPreview
            const payloadAsPreview: Partial<CanvasPreview> = {
              id: otherCanvasData.id,
              assigned_user_id: otherCanvasData.assigned_user_id ?? "",
              labels: otherCanvasData.labels as string[],
              project_id: otherCanvasData.project_id ?? "",
              env: otherCanvasData.env ?? "",
              current_branch: otherCanvasData.current_branch ?? "",
              owner_user_id: otherCanvasData.owner_user_id ?? "",
              last_updated: otherCanvasData.last_updated ?? "",
            };

            const issue = get().canvasIdToIssue[canvasId];
            if (issue) {
              get().syncCanvasDataForIssues([
                {
                  ...issue.canvas,
                  ...payloadAsPreview,
                },
              ]);
            }
          }
        } catch (error) {
          console.error("Error updating storyboards optimistically:", error);

          set({
            sbUpdateLock: {
              ...get().sbUpdateLock,
              [canvasId]: false,
            },
          });

          return;
        }

        set({
          sbUpdateLock: {
            ...get().sbUpdateLock,
            [canvasId]: false,
          },
        });

        const updateSupabase = async () => {
          set({
            sbDBUpdateLock: {
              ...get().sbDBUpdateLock,
              [canvasId]: true,
            },
          });

          const onError = (error) => {
            console.error("Error updating storyboards:", error);
            toast.error("Error updating storyboards, please refresh the page");
          };

          try {
            await useSupabaseStore
              .getState()
              .supabaseClient?.from("canvases")
              .update({
                storyboards: newStoryboards,
                ...(otherCanvasData || {}),
              })
              .eq("id", canvasId)
              .then(({ error }) => {
                if (error) onError(error);
              });
          } catch (error) {
            onError(error);
          }

          set({
            sbDBUpdateLock: {
              ...get().sbDBUpdateLock,
              [canvasId]: false,
            },
          });
        };

        const dbLock = get().sbDBUpdateLock[canvasId];
        if (dbLock) {
          // wait for the lock to be released
          const unsubscribe = useIssuesStore.subscribe((state) => {
            const lock = state.sbDBUpdateLock[canvasId];
            if (!lock) {
              unsubscribe();
              updateSupabase();
            }
          });
          return;
        }

        updateSupabase();
      },
      createFeaturePRD: (
        supabaseClient: SupabaseClient | undefined,
        featurePRDPayload: FeaturePRD,
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        if (!featurePRDPayload.canvas_id) {
          console.error("Canvas ID is required");
          return;
        }

        if (!featurePRDPayload.id) {
          console.error("Feature PRD ID is required");
          return;
        }

        // Store current state before update
        const originalIssue =
          state.canvasIdToIssue[featurePRDPayload.canvas_id];

        // Update UI optimistically
        state.syncFeaturePrdData([featurePRDPayload]);

        supabaseClient
          .from("feature_prd")
          .insert(featurePRDPayload)
          .then(({ error }) => {
            if (error) {
              console.error("Error creating feature PRD:", error);
              // Roll back to original state if we had one
              if (originalIssue?.featurePrd) {
                state.syncFeaturePrdData([originalIssue.featurePrd]);
              }
              return;
            }
          });
      },
      updateFeaturePRD: (
        supabaseClient: SupabaseClient | undefined,
        featurePRDPayload: FeaturePRD,
      ) => {
        const state = get();

        if (!supabaseClient) {
          console.error("Supabase client not initialized");
          return;
        }

        if (!featurePRDPayload.canvas_id) {
          console.error("Canvas ID is required");
          return;
        }

        if (!featurePRDPayload.id) {
          console.error("Feature PRD ID is required");
          return;
        }

        const issue = state.canvasIdToIssue[featurePRDPayload.canvas_id];
        if (!issue) {
          return;
        }

        const originalPrd = issue.featurePrd;
        if (!originalPrd) {
          return;
        }

        // Create updated PRD
        const updatedPrd = {
          ...originalPrd,
          ...featurePRDPayload,
        };

        // Update UI optimistically
        state.syncFeaturePrdData([updatedPrd]);

        supabaseClient
          .from("feature_prd")
          .update(featurePRDPayload)
          .eq("canvas_id", featurePRDPayload.canvas_id)
          .then(({ error }) => {
            if (error) {
              console.error("Error updating feature PRD:", error);
              // Roll back to original state
              state.syncFeaturePrdData([originalPrd]);
              return;
            }
          });
      },

      isProjectStatusProcessingComplete: (projectId: string) => {
        const state = get();

        if (state.projectStatusProcessingComplete[projectId]) {
          return true;
        }

        const projectIssues = state.issuesByProject[projectId];
        if (!projectIssues || Object.keys(projectIssues).length === 0) {
          return true;
        }

        return false;
      },

      updateFeaturePrdStatuses: async (
        supabaseClient: SupabaseClient | undefined,
        projectId: string,
        oldStatusName: string,
        newStatusName: string,
      ) => {
        const trimmedOldStatus = oldStatusName.trim();
        const trimmedNewStatus = newStatusName.trim();

        if (trimmedOldStatus === trimmedNewStatus) {
          return;
        }

        if (!supabaseClient) {
          throw new Error("Supabase client not available");
        }

        const { data: projectCanvasIds, error: canvasFetchError } =
          await supabaseClient
            .from("canvases")
            .select("id")
            .eq("project_id", projectId);

        if (canvasFetchError) {
          throw new Error(
            `Failed to fetch project canvas IDs: ${canvasFetchError.message}`,
          );
        }

        if (!projectCanvasIds?.length) {
          return;
        }

        const canvasIds = projectCanvasIds.map((canvas) => canvas.id);
        const BATCH_SIZE = 50;

        for (let i = 0; i < canvasIds.length; i += BATCH_SIZE) {
          const batch = canvasIds.slice(i, i + BATCH_SIZE);
          const { error } = await supabaseClient
            .from("feature_prd")
            .update({ status: trimmedNewStatus })
            .in("canvas_id", batch)
            .eq("status", trimmedOldStatus);

          if (error) {
            throw new Error(
              `Failed to update canvas statuses: ${error.message}`,
            );
          }
        }
      },

      updateUIStateWithNewStatuses: (
        oldStatusName: string,
        newStatusName: string,
        issuesToUpdate: Issue[],
      ) => {
        if (issuesToUpdate.length > 0) {
          const issuesWithOldStatus = issuesToUpdate.filter(
            (issue) => issue.featurePrd?.status === oldStatusName,
          );

          if (issuesWithOldStatus.length > 0) {
            const updatedFeaturePrds = issuesWithOldStatus.map((issue) => ({
              ...issue.featurePrd,
              status: newStatusName,
            }));

            get().syncFeaturePrdData(updatedFeaturePrds);
          }
        }
      },
    }),
    {
      name: "issues-canvas-storage",
      storage: createFilteredStorage<Pick<IssuesState, "currentCanvas">>(
        (state) => ({
          ...state,
          currentCanvas: filterCurrentCanvasByRecent(state.currentCanvas),
        }),
      ),
      partialize: (state) => ({
        currentCanvas: state.currentCanvas,
      }),
    },
  ),
);

export const applyStoryboardDeltas = (
  storyboardDeltas: StoryboardDelta[],
  storyboards: Storyboard[],
  valuesAtStartOfAction?: Record<string, Partial<Storyboard>>,
): {
  newStoryboards: Storyboard[];
  undoDeltas: StoryboardDelta[];
} => {
  const newStoryboards = [...storyboards];
  const undoDeltas: StoryboardDelta[] = [];

  for (const delta of storyboardDeltas) {
    if (delta.changeType === "upsert" || delta.changeType === "update") {
      const index = newStoryboards.findIndex(
        (sb) => sb.id === delta.storyboardId,
      );
      if (index !== -1) {
        // Get previous values to undo to
        const prevValues = Object.keys(delta.storyboardUpdateDict).reduce(
          (acc, key) => {
            acc[key] =
              valuesAtStartOfAction?.[delta.storyboardId]?.[key] ??
              newStoryboards[index][key];
            return acc;
          },
          {},
        );

        undoDeltas.push({
          ...delta,
          changeType: "update",
          storyboardUpdateDict: prevValues,
        });

        newStoryboards[index] = {
          ...newStoryboards[index],
          ...delta.storyboardUpdateDict,
        };
      } else if (delta.changeType === "upsert") {
        // Upsert only - update does not create a new storyboard

        // Undo is delete
        undoDeltas.push({
          ...delta,
          changeType: "remove",
          storyboardUpdateDict: {},
        });

        newStoryboards.push(delta.storyboardUpdateDict as Storyboard);
      }
    } else if (delta.changeType === "remove") {
      const index = newStoryboards.findIndex(
        (sb) => sb.id === delta.storyboardId,
      );
      if (index !== -1) {
        const deletedStoryboards = newStoryboards.splice(index, 1);

        // Undo is insert
        undoDeltas.push({
          ...delta,
          changeType: "upsert",
          storyboardUpdateDict: deletedStoryboards[0],
        });
      }
    }
  }

  return {
    newStoryboards,
    undoDeltas,
  };
};
